import { useAuth } from '../../auth.context';
import { useMemo, useRef } from 'react';
import {
  PlayerProfile,
  PlayerInjury,
  PlayerTransfer,
  PlayerAchievement,
  PlayerStats
} from '../types/kyp-search.types';

export interface PlayerDataServiceDeps {
  getToken: () => string | null;
  signal?: AbortSignal;
}

export class PlayerDataService {
  private fetchedSupplementaryDataSet = new Set<string>();
  
  constructor(private deps: PlayerDataServiceDeps) {}

  async fetchPlayerProfile(id: string): Promise<PlayerProfile | null> {
    try {
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      console.log(`Fetching player profile for ID: ${id}`);
      const profileResponse = await fetch(`${baseUrl}/ai/v1/player/${id}/profile`, {
        credentials: 'include',
        headers: requestHeaders,
        signal: this.deps.signal
      });

      if (!profileResponse.ok) {
        throw new Error(`HTTP error! status: ${profileResponse.status}`);
      }
      
      const profile = await profileResponse.json();
      console.log(`Successfully fetched player profile for: ${profile.name}`);
      return profile;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Player profile request was aborted');
        return null;
      }
      console.error('Error fetching player profile:', error);
      return null;
    }
  }
  
  async fetchPlayerStats(id: string): Promise<PlayerStats[] | null> {
    try {
      // Import the fetchJsonWithRetry utility
      const { fetchJsonWithRetry } = await import('../../fetchWithRetry');
      
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      // Configure retry options
      const retryOptions = {
        maxRetries: 3,
        retryDelay: 500,
        headers: requestHeaders,
        signal: this.deps.signal
      };
      
      console.log(`Fetching statistics for player ID: ${id}`);
      const stats = await fetchJsonWithRetry<PlayerStats[]>(
        `${baseUrl}/ai/v1/player/${id}/stats`, 
        retryOptions
      );
      
      console.log(`Received statistics data:`, stats ? `${stats.length} items` : 'null');
      return stats;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Player stats request was aborted');
        return null;
      }
      console.error('Error fetching player statistics:', error);
      return null;
    }
  }
  
  async fetchPlayerInjuries(id: string): Promise<PlayerInjury[] | null> {
    try {
      // Import the fetchJsonWithRetry utility
      const { fetchJsonWithRetry } = await import('../../fetchWithRetry');
      
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      // Configure retry options
      const retryOptions = {
        maxRetries: 3,
        retryDelay: 500,
        headers: requestHeaders,
        signal: this.deps.signal
      };
      
      console.log(`Fetching injuries for player ID: ${id}`);
      const injuries = await fetchJsonWithRetry<PlayerInjury[]>(
        `${baseUrl}/ai/v1/player/${id}/injuries`, 
        retryOptions
      );
      
      console.log(`Received injuries data:`, injuries ? `${injuries.length} items` : 'null');
      return injuries;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Player injuries request was aborted');
        return null;
      }
      console.error('Error fetching player injuries:', error);
      return null;
    }
  }
  
  async fetchPlayerTransfers(id: string): Promise<PlayerTransfer[] | null> {
    try {
      // Import the fetchJsonWithRetry utility
      const { fetchJsonWithRetry } = await import('../../fetchWithRetry');
      
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      // Configure retry options
      const retryOptions = {
        maxRetries: 3,
        retryDelay: 500,
        headers: requestHeaders,
        signal: this.deps.signal
      };
      
      console.log(`Fetching transfers for player ID: ${id}`);
      const transfers = await fetchJsonWithRetry<PlayerTransfer[]>(
        `${baseUrl}/ai/v1/player/${id}/transfers`, 
        retryOptions
      );
      
      console.log(`Received transfers data:`, transfers ? `${transfers.length} items` : 'null');
      return transfers;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Player transfers request was aborted');
        return null;
      }
      console.error('Error fetching player transfers:', error);
      return null;
    }
  }
  
  async fetchPlayerAchievements(id: string): Promise<PlayerAchievement[] | null> {
    try {
      // Import the fetchJsonWithRetry utility
      const { fetchJsonWithRetry } = await import('../../fetchWithRetry');
      
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;
      
      // Configure retry options
      const retryOptions = {
        maxRetries: 3,
        retryDelay: 500,
        headers: requestHeaders,
        signal: this.deps.signal
      };
      
      console.log(`Fetching achievements for player ID: ${id}`);
      const achievements = await fetchJsonWithRetry<PlayerAchievement[]>(
        `${baseUrl}/ai/v1/player/${id}/achievements`, 
        retryOptions
      );
      
      console.log(`Received achievements data:`, achievements ? `${achievements.length} items` : 'null');
      return achievements;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Player achievements request was aborted');
        return null;
      }
      console.error('Error fetching player achievements:', error);
      return null;
    }
  }
  
  async fetchSupplementaryData(id: string): Promise<{
    injuries: PlayerInjury[] | null;
    transfers: PlayerTransfer[] | null;
    achievements: PlayerAchievement[] | null;
    stats: PlayerStats[] | null;
  }> {
    if (!id || id.trim() === '') {
      console.error('Cannot fetch supplementary data: Invalid player ID');
      return {
        injuries: null,
        transfers: null,
        achievements: null,
        stats: null
      };
    }
    
    // Create a unique key for this player's supplementary data
    const supplementaryDataKey = `supplementary-${id}`;
    
    // Check if we've already fetched this data
    if (this.fetchedSupplementaryDataSet.has(supplementaryDataKey)) {
      console.log(`Supplementary data for player ID: ${id} already fetched, skipping`);
      return {
        injuries: null,
        transfers: null,
        achievements: null,
        stats: null
      };
    }
    
    // Mark this data as being fetched
    this.fetchedSupplementaryDataSet.add(supplementaryDataKey);
    
    console.log(`Starting to fetch supplementary data for player ID: ${id}`);
    
    try {
      // Create an array of promises for parallel fetching
      const [injuries, transfers, achievements, stats] = await Promise.all([
        this.fetchPlayerInjuries(id),
        this.fetchPlayerTransfers(id),
        this.fetchPlayerAchievements(id),
        this.fetchPlayerStats(id)
      ]);
      
      console.log(`Successfully fetched all supplementary data for player ID: ${id}`);
      
      return {
        injuries,
        transfers,
        achievements,
        stats
      };
    } catch (error) {
      console.error('Error fetching supplementary data:', error);
      // Remove from fetched set on error so we can try again
      this.fetchedSupplementaryDataSet.delete(supplementaryDataKey);
      
      return {
        injuries: null,
        transfers: null,
        achievements: null,
        stats: null
      };
    }
  }
  
  async prefetchTabData(id: string): Promise<void> {
    try {
      // Import the fetchJsonWithRetry utility
      const { fetchJsonWithRetry } = await import('../../fetchWithRetry');
      
      const token = this.deps.getToken();
      if (!token) {
        throw new Error('Not authenticated');
      }

      const requestHeaders = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      // In development, use relative URLs to leverage the Vite proxy
      const baseUrl = import.meta.env.DEV ? '' : import.meta.env.VITE_API_URL;

      // Configure retry options
      const retryOptions = {
        maxRetries: 3,
        retryDelay: 500,
        headers: requestHeaders,
        signal: this.deps.signal
      };

      // Import the queryClient
      const { queryClient } = await import('../../queryClient');

      // Create an array of promises for all data fetching operations
      const fetchPromises = [
        // Current Season Stats
        (async () => {
          try {
            console.log(`Pre-fetching current season stats for player ID: ${id}`);
            const currentSeasonStats = await fetchJsonWithRetry(
              `${baseUrl}/ai/v1/player/${id}/current-season-stats`,
              retryOptions
            );
            console.log('Successfully pre-fetched current season stats');
            
            // Populate the React Query cache
            queryClient.setQueryData(['playerCurrentSeasonStats', id], currentSeasonStats);
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              console.log('Current season stats request was aborted - likely due to navigation or unmounting');
            } else {
              console.error('Error pre-fetching current season stats:', error);
            }
          }
        })(),
        
        // Competition Grade Cards
        (async () => {
          try {
            console.log(`Pre-fetching competition stats for player ID: ${id}`);
            const competitionStats = await fetchJsonWithRetry(
              `${baseUrl}/ai/v1/player/${id}/competition-stats`,
              retryOptions
            );
            console.log('Successfully pre-fetched competition stats');
            
            // Populate the React Query cache
            queryClient.setQueryData(['playerCompetitionStats', id], competitionStats);
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              console.log('Competition stats request was aborted - likely due to navigation or unmounting');
            } else {
              console.error('Error pre-fetching competition stats:', error);
            }
          }
        })(),
        
        // Transfer Impacts
        (async () => {
          try {
            console.log(`Pre-fetching transfer stats for player ID: ${id}`);
            const transferStats = await fetchJsonWithRetry(
              `${baseUrl}/ai/v1/player/${id}/transfer-stats`,
              retryOptions
            );
            console.log('Successfully pre-fetched transfer stats');
            
            // Populate the React Query cache
            queryClient.setQueryData(['playerTransferStats', id], transferStats);
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              console.log('Transfer stats request was aborted - likely due to navigation or unmounting');
            } else {
              console.error('Error pre-fetching transfer stats:', error);
            }
          }
        })(),
        
        // Availability Timeline
        (async () => {
          try {
            console.log(`Pre-fetching availability timeline for player ID: ${id}`);
            const availabilityTimeline = await fetchJsonWithRetry(
              `${baseUrl}/ai/v1/player/${id}/availability-timeline`,
              retryOptions
            );
            console.log('Successfully pre-fetched availability timeline');
            
            // Populate the React Query cache
            queryClient.setQueryData(['playerAvailabilityTimeline', id], availabilityTimeline);
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              console.log('Availability timeline request was aborted - likely due to navigation or unmounting');
            } else {
              console.error('Error pre-fetching availability timeline:', error);
            }
          }
        })(),
        
        // Market Value Momentum
        (async () => {
          try {
            console.log(`Pre-fetching market value momentum data for player ID: ${id}`);
            const marketValueMomentum = await fetchJsonWithRetry(
              `${baseUrl}/ai/v1/player/${id}/market-value-momentum`,
              retryOptions
            );
            console.log('Successfully pre-fetched market value momentum data');
            
            // Populate the React Query cache
            queryClient.setQueryData(['playerMarketValueMomentum', id], marketValueMomentum);
          } catch (error) {
            if (error instanceof DOMException && error.name === 'AbortError') {
              console.log('Market value momentum request was aborted - likely due to navigation or unmounting');
            } else {
              console.error('Error pre-fetching market value momentum data:', error);
            }
          }
        })(),
        
        // Club League Information
        (async () => {
          try {
            console.log(`Pre-fetching club league info for player ID: ${id}`);
            const clubLeagueInfo = await fetchJsonWithRetry(
              `${baseUrl}/ai/v1/player/${id}/club-league-info`,
              retryOptions
            );
            console.log('Successfully pre-fetched club league info');
            
            // Populate the React Query cache
            queryClient.setQueryData(['playerClubLeagueInfo', id], clubLeagueInfo);
          } catch (error) {
            // This endpoint might not exist yet, so check if it's an abort error first
            if (error instanceof DOMException && error.name === 'AbortError') {
              console.log('Club league info request was aborted - likely due to navigation or unmounting');
            } else {
              // For this endpoint specifically, it might not exist yet, so use a softer log level
              console.log('Note: Club league info endpoint might not be available:', error);
            }
          }
        })()
      ];
      
      // Wait for all prefetch operations to complete
      await Promise.allSettled(fetchPromises);
      console.log(`Completed prefetching tab data for player ID: ${id}`);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.log('Tab data prefetching was aborted - likely due to navigation or unmounting');
      } else {
        console.error('Error during tab data prefetching:', error);
      }
    }
  }
  
  calculateCareerStats(stats: PlayerStats[] | null): Partial<PlayerStats> | null {
    if (!stats || stats.length === 0) {
      return null;
    }

    // Aggregate all stats
    return stats.reduce((acc, stat) => {
      return {
        ...acc,
        appearances: acc.appearances + stat.appearances,
        goals: acc.goals + stat.goals,
        assists: acc.assists + stat.assists,
        yellow_cards: acc.yellow_cards + stat.yellow_cards,
        red_cards: acc.red_cards + stat.red_cards,
        minutes_played: acc.minutes_played + stat.minutes_played
      };
    }, {
      appearances: 0,
      goals: 0,
      assists: 0,
      yellow_cards: 0,
      red_cards: 0,
      minutes_played: 0
    } as Partial<PlayerStats>);
  }
}

export const usePlayerDataService = () => {
  const { getToken } = useAuth();
  const abortControllerRef = useRef<AbortController | null>(null);
  
  // Create a new abort controller if needed
  if (!abortControllerRef.current) {
    abortControllerRef.current = new AbortController();
  }
  
  return useMemo(() => {
    const service = new PlayerDataService({ 
      getToken, 
      signal: abortControllerRef.current?.signal 
    });
    
    // Create an object that has all the methods from the service
    // plus an abort method, properly preserving the prototype chain
    const serviceWithAbort = Object.create(Object.getPrototypeOf(service), {
      ...Object.getOwnPropertyDescriptors(service),
      abort: {
        value: () => {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = new AbortController();
          }
        },
        enumerable: true,
        configurable: true,
        writable: true
      }
    });
    
    return serviceWithAbort;
  }, [getToken]);
};